import Decimal from "decimal.js"
import PropTypes from "prop-types"
import React, { createContext } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useMutation } from "react-query"

import Button from "src/components/Button"
import Form from "src/components/Form"

import { updateGeneralSettings } from "src/api/Settings/General"

import { useToast } from "src/hooks/use_toast"
import useUnsavedChangesWarning from "src/hooks/use_unsaved_changes_warning"

import BoaterPayNow from "./BoaterPayNow"
import InsuranceAndRegistration from "./InsuranceAndRegistration"
import Surcharges from "./Surcharges"

export const GeneralSettingsContext = createContext(null)

const General = (props) => {
  const {
    marina,
    canUpdateSettings,
    surcharge,
    marinaSurchargeEligible,
    canConfigurePosSurcharge,
    insuranceAndRegistrationOptions,
    payNow: {
      contractsEnabled,
      reservationsEnabled,
      marinaHasAnyPosFeaturesEnabled,
      posSalesEnabled,
      isActivatedAccount,
    },
    useNewTaxExperience,
  } = props
  const marinaSlug = marina.slug
  const showToast = useToast()
  const methods = useForm({
    defaultValues: {
      boater_pay_now_contracts_enabled: contractsEnabled,
      boater_pay_now_point_of_sale_sales_enabled: posSalesEnabled,
      boater_pay_now_reservations_enabled: reservationsEnabled,
      insurance_requirement_for_contracts:
        marina.insurance_requirement_for_contracts,
      registration_requirement_for_contracts:
        marina.registration_requirement_for_contracts,
      convenience_fee_product_id: surcharge?.productId || "",
      convenience_fee_percent: surcharge
        ? new Decimal(surcharge.percent).mul(100)
        : "",
      convenience_fees_contracts_enabled: !!surcharge?.contractsEnabled,
      convenience_fees_point_of_sale_enabled: !!surcharge?.pointOfSaleEnabled,
    },
  })

  const { mutate: mutateGeneralSettings, isLoading: isLoadingGeneralSettings } =
    useMutation({
      queryKey: ["generalSettings", marinaSlug],
      mutationFn: (data) => updateGeneralSettings({ marinaSlug, data }),
      onSuccess: () => {
        showToast("Setting successfully updated!", {
          type: "success",
        })
      },
      onError: () => {
        showToast(
          "An error occurred while updating settings. Please try again or contact mayday@dockwa.com for assistance.",
          {
            type: "error",
            duration: 12,
          }
        )
      },
    })

  const onSubmit = (values) => {
    const formDataAsParams = {
      configuration: {
        boater_pay_now_contracts_enabled:
          values.boater_pay_now_contracts_enabled,
        boater_pay_now_reservations_enabled:
          values.boater_pay_now_reservations_enabled,
        boater_pay_now_point_of_sale_sales_enabled:
          values.boater_pay_now_point_of_sale_sales_enabled,
      },
      marina: {
        insurance_requirement_for_contracts:
          values.insurance_requirement_for_contracts,
        registration_requirement_for_contracts:
          values.registration_requirement_for_contracts,
      },
    }

    if (marinaSurchargeEligible) {
      const contractsEnabled = values.convenience_fees_contracts_enabled
      const posEnabled = values.convenience_fees_point_of_sale_enabled
      const anyEnabled = contractsEnabled || posEnabled
      const feeProductId = anyEnabled ? values.convenience_fee_product_id : ""
      const feePercent = anyEnabled
        ? new Decimal(values.convenience_fee_percent).div(100)
        : ""

      formDataAsParams.convenience_fee = {
        convenience_fee_product_id: feeProductId,
        convenience_fee_percent: feePercent,
        convenience_fees_contracts_enabled: contractsEnabled,
        convenience_fees_point_of_sale_enabled: posEnabled,
      }
    }
    mutateGeneralSettings(formDataAsParams)
  }

  useUnsavedChangesWarning(
    methods.formState.isDirty,
    methods.formState.isSubmitSuccessful
  )

  return (
    <GeneralSettingsContext.Provider
      value={{
        canConfigurePosSurcharge,
        canUpdateSettings,
        insuranceAndRegistrationOptions,
        marina,
        marinaHasAnyPosFeaturesEnabled,
        useNewTaxExperience,
      }}
    >
      <Form onSubmit={methods.handleSubmit(onSubmit)}>
        <FormProvider {...methods}>
          <InsuranceAndRegistration
            canUpdateSettings={canUpdateSettings}
            insuranceAndRegistrationOptions={insuranceAndRegistrationOptions}
          />
          {isActivatedAccount ? (
            <>
              <hr className="mt-8" />
              <BoaterPayNow
                marinaHasAnyPosFeaturesEnabled={marinaHasAnyPosFeaturesEnabled}
              />
            </>
          ) : null}
          {marinaSurchargeEligible ? (
            <>
              <hr className="mt-8" />
              <Surcharges />
            </>
          ) : null}
          <div className="mt-8">
            <Button
              variant="primary"
              type="submit"
              disabled={!canUpdateSettings || isLoadingGeneralSettings}
            >
              Save
            </Button>
          </div>
        </FormProvider>
      </Form>
    </GeneralSettingsContext.Provider>
  )
}

General.propTypes = {
  marina: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    insurance_requirement_for_contracts: PropTypes.string.isRequired,
    registration_requirement_for_contracts: PropTypes.string.isRequired,
  }).isRequired,
  canUpdateSettings: PropTypes.bool.isRequired,
  surcharge: PropTypes.shape({
    percent: PropTypes.string.isRequired,
    productId: PropTypes.string.isRequired,
    contractsEnabled: PropTypes.bool.isRequired,
    pointOfSaleEnabled: PropTypes.bool.isRequired,
  }),
  marinaSurchargeEligible: PropTypes.bool.isRequired,
  canConfigurePosSurcharge: PropTypes.bool.isRequired,
  insuranceAndRegistrationOptions: PropTypes.shape({
    none: PropTypes.string.isRequired,
    info: PropTypes.string.isRequired,
    info_and_upload: PropTypes.string.isRequired,
  }).isRequired,
  payNow: PropTypes.shape({
    contractsEnabled: PropTypes.bool.isRequired,
    reservationsEnabled: PropTypes.bool.isRequired,
    posSalesEnabled: PropTypes.bool.isRequired,
    marinaHasAnyPosFeaturesEnabled: PropTypes.bool.isRequired,
    isActivatedAccount: PropTypes.bool.isRequired,
  }).isRequired,
  useNewTaxExperience: PropTypes.bool.isRequired,
}

export default General
